/**
 * Vehicle details modal. Replaces `vehicle-details.js::openVehicleDetailsModal`
 * + `renderGovDetails` + the kv-table helpers.
 *
 * Reads the cached gov bundle from `vehicles/{plate}/gov` and renders it as
 * a key/value table. The modal DOM is created on-demand (matches legacy).
 */

import { auth, ref, ensureAuthReady } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { escape } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('vehicleDetails');

const OVERLAY_ID = 'vehicleDetailsOverlay';

const LABELS_HE = /** @type {Record<string, string>} */ ({
  mispar_rechev: 'מספר רכב',
  shnat_yitzur: 'שנת יצור',
  tozeret_cd: 'קוד יצרן',
  tozeret_nm: 'יצרן',
  kinuy_mishari: 'דגם',
  degem_cd: 'קוד דגם',
  degem_nm: 'דגם יצרן',
  degem_manoa: 'קוד מנוע/תת־דגם',
  sug_delek_cd: 'קוד דלק',
  sug_delek_nm: 'סוג דלק',
  sug_degem: 'סוג דגם',
  baalut: 'בעלות',
  ramat_gimur: 'רמת גימור',
  ramat_eivzur_betihuty: 'בטיחות',
  kvutzat_zihum: 'קבוצת זיהום',
  tzeva_cd: 'צבע (קוד)',
  tzeva_rechev: 'צבע',
  zmig_kidmi: 'צמיג קדמי',
  zmig_ahori: 'צמיג אחורי',
  misgeret: 'מספר שלדה',
  horaat_rishum: 'הוראת רישום',
  tokef_dt: 'תוקף רישיון',
  mivchan_acharon_dt: 'מבחן אחרון',
  moed_aliya_lakvish: 'עלה לכביש',
  vehicleLastSyncISO: 'סנכרון אחרון',
});

const VALUE_MAPS = /** @type {Record<string, Record<string, string>>} */ ({
  sug_degem: { P: 'פרטי', M: 'משא', O: 'אופנוע', B: 'אוטובוס' },
  sug_delek_nm: {
    Electric: 'חשמל', Gasoline: 'בנזין', Diesel: 'דיזל',
    Hybrid: 'היברידי', 'Plug-in Hybrid': 'היברידי נטען',
    CNG: 'גז דחוס', LPG: 'גפ״מ',
  },
  baalut: { PRIV: 'פרטי', LEAS: 'ליסינג', RENT: 'השכרה' },
});

const HIDE_KEYS = new Set(['_id', 'rank']);

/** Open the vehicle details modal for the current user's plate. */
export async function open() {
  try { await ensureAuthReady(); } catch { /* ignore */ }

  const overlay = ensureModal();
  // Re-append to <body> on every open. The other drawers/modals are also at
  // z-index var(--z-modal), so when stacking ties, last-in-DOM wins. Without
  // this move, opening "הרכב שלי" after this overlay was already created
  // once leaves the details overlay buried behind the newer drawer.
  document.body.appendChild(overlay);
  const body = /** @type {HTMLElement} */ (overlay.querySelector('#vehDetailsBody'));

  const plate = await resolvePlate();
  if (!plate) {
    body.innerHTML = `<div class="details-muted">לא נמצאה לוחית להצגה.</div>`;
    overlay.classList.add('open');
    return;
  }

  try {
    const snap = await ref(`vehicles/${plate}/gov`).once('value');
    const gov = snap.val() ?? null;
    body.innerHTML = renderGovDetails(gov);
  } catch (err) {
    log.warn('gov read failed:', err);
    body.innerHTML = `<div class="details-muted">שגיאה בטעינת פרטים.</div>`;
  }
  overlay.classList.add('open');
}

/** Close the modal. */
export function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) overlay.classList.remove('open');
}

// ====================================================================
// internals
// ====================================================================

function ensureModal() {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'details-overlay';
  overlay.innerHTML = `
    <aside class="details-modal" role="dialog" aria-modal="true" aria-labelledby="vehDetailsTitle">
      <header class="details-header">
        <div id="vehDetailsTitle" class="details-title">פרטי הרכב</div>
        <button class="details-close" id="vehDetailsCloseBtn" aria-label="סגור">✕</button>
      </header>
      <div id="vehDetailsBody" class="details-body">
        <div class="details-muted">טוען פרטים...</div>
      </div>
    </aside>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('#vehDetailsCloseBtn')?.addEventListener('click', close);
  return overlay;
}

async function resolvePlate() {
  const local = normalizePlate(LocalState.plate ?? '');
  if (local) return local;
  const uid = auth().currentUser?.uid;
  if (!uid) return null;
  try {
    const u = (await ref(`users/${uid}`).once('value')).val() ?? {};
    return normalizePlate(u.plate ?? '');
  } catch { return null; }
}

/**
 * @param {{ vehicle?: Record<string, unknown>; ownership?: any[]; disabled?: any; vehicleLastSyncISO?: string } | null} gov
 */
function renderGovDetails(gov) {
  if (!gov) return `<div class="details-muted">אין פרטים זמינים. נסה לרענן.</div>`;
  const veh = gov.vehicle ?? {};
  const fetchedAt = gov.vehicleLastSyncISO
    ? new Date(gov.vehicleLastSyncISO).toLocaleString('he-IL')
    : '—';

  const rows = Object.entries(veh)
    .filter(([k]) => !HIDE_KEYS.has(k))
    .map(([k, v]) => kvRow(LABELS_HE[k] ?? k, translateValue(k, v)))
    .join('');

  const vehicleSection = rows
    ? `<section class="details-section"><h4>פרטי רכב</h4><div class="kv">${rows}</div></section>`
    : '';

  let ownershipSection = '';
  if (Array.isArray(gov.ownership) && gov.ownership.length > 0) {
    const ownerRows = gov.ownership.slice(0, 5).map((rec, idx) =>
      kvRow(`רשומת בעלות ${idx + 1}`, prettyDate(rec?.baalut_dt))
    ).join('');
    ownershipSection = `<section class="details-section"><h4>היסטוריית בעלות</h4><div class="kv">${ownerRows}</div></section>`;
  }

  const metaSection = `
    <section class="details-section">
      <h4>מטה־נתונים</h4>
      <div class="kv">${kvRow('עדכון אחרון', fetchedAt)}</div>
    </section>
  `;

  return vehicleSection + ownershipSection + metaSection;
}

/** @param {string} key @param {string} val */
function kvRow(key, val) {
  const safe = (val == null || val === '') ? '—' : String(val);
  return `<div class="k">${escape(key)}</div><div class="v">${escape(safe)}</div>`;
}

/** @param {string} key @param {unknown} val */
function translateValue(key, val) {
  if (val == null || val === '') return '—';
  if (key === 'tokef_dt' || key === 'mivchan_acharon_dt') return prettyDate(val);
  if (key === 'vehicleLastSyncISO') {
    const d = new Date(String(val));
    return Number.isFinite(d.getTime()) ? d.toLocaleDateString('he-IL') : String(val);
  }
  const map = VALUE_MAPS[key];
  if (map && typeof val === 'string' && map[val]) return map[val];
  return String(val);
}

/** @param {unknown} x */
function prettyDate(x) {
  if (!x) return '';
  const s = String(x).trim();
  if (/^\d{8}$/.test(s)) {
    return `${s.slice(6, 8)}.${s.slice(4, 6)}.${s.slice(0, 4)}`;
  }
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('he-IL') : s;
}
